import { useCallback } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { useEnableOnTestEnvs } from '@components/Web3/CreateTown/components/DurationSwitcher'
import {
    EMPTY_TOKEN_ID,
    useMembershipSubscriptionsStore,
} from '@components/Web3/Subscriptions/membershipSubscriptions.store'
import { useAutoRenewalSubscription } from 'hooks/useAutoRenewalSubscription'
import {
    blockchainQueryKeys,
    queryClient,
    SpaceAddressFromSpaceId,
    TSigner,
} from 'use-towns-client'
import { Address } from 'viem'

import { useFetchMyMembershipStatusAsync } from './useFetchMembershipStatusAsync'

export function useInstallSubscriptionAfterJoin({ spaceId }: { spaceId: string | undefined }) {
    const addFailedSubscription = useMembershipSubscriptionsStore((s) => s.addFailedSubscription)
    const enableOnTestEnvs = useEnableOnTestEnvs()
    const fetchMembershipStatusAsync = useFetchMyMembershipStatusAsync({ spaceId })
    const { installAutoRenewal } = useAutoRenewalSubscription({
        onSuccess: ({ spaceAddress, tokenId, myAddress }) => {
            if (myAddress) {
                queryClient.refetchQueries({
                    queryKey: blockchainQueryKeys.subscriptions(myAddress),
                })
            }
        },
        onError: ({ spaceAddress, tokenId, error }) => {
            console.error(`[useInstallSubscriptionAfterJoin] error installing auto-renewal`, error)
            addFailedSubscription(spaceAddress, tokenId, error.message)
            if (enableOnTestEnvs) {
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message="Subscription installed failed"
                        subMessage="This popup only appears on test environments."
                    />
                ))
            }
        },
    })

    return useCallback(
        ({ userId, signer }: { userId: string; signer: TSigner }) => {
            fetchMembershipStatusAsync({ userId })
                .then((membership) => {
                    const tokenId = membership.tokenId
                    if (!tokenId) {
                        console.error(`[useInstallSubscriptionAfterJoin] no tokenId found`, {
                            spaceId,
                        })
                        if (spaceId) {
                            addFailedSubscription(
                                SpaceAddressFromSpaceId(spaceId) as Address,
                                EMPTY_TOKEN_ID,
                                'No tokenId found after fetching membership status',
                            )
                        }
                    } else if (!spaceId) {
                        console.error(`[useInstallSubscriptionAfterJoin] no spaceId available`, {
                            tokenId,
                        })
                    } else {
                        installAutoRenewal({
                            spaceId,
                            tokenId,
                            signer,
                        })
                    }
                })
                .catch((error) => {
                    console.error(
                        `[useInstallSubscriptionAfterJoin] error fetching membership status`,
                        error,
                    )
                })
        },
        [installAutoRenewal, fetchMembershipStatusAsync, spaceId, addFailedSubscription],
    )
}
